import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";
import type { RelatedLink } from "@/components/RelatedPages";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const hfdFaqs: LearnFAQ[] = [
  {
    question: "What are the most common depression symptoms?",
    answer:
      "Persistent low or flat mood, loss of interest or pleasure, fatigue that rest doesn't fix, changes in sleep or appetite, trouble concentrating, irritability, and feelings of hopelessness or worthlessness. In high-functioning depression these symptoms are often milder but last for years.",
  },
  {
    question: "How to treat depression when you're still functioning?",
    answer:
      "Start with an evaluation by a psychiatrist or primary care provider. First-line depression treatment is usually depression therapy, medication or both. If you've tried antidepressants without enough relief, ask about TMS and accelerated protocols like SAINT®.",
  },
  {
    question: "What is treatment resistant depression?",
    answer:
      "It generally means depression that hasn't improved enough after adequate trials of antidepressant medication, often two or more. It's common and doesn't mean you're out of options.",
  },
  {
    question: "Is depression therapy enough on its own?",
    answer:
      "For many people, yes, especially with milder symptoms. Others do best combining therapy with medication or brain stimulation. A clinician can help you decide.",
  },
  {
    question: "How fast does accelerated TMS work?",
    answer:
      "SAINT® is completed in five consecutive days, and Ampa One™ in as few as two. In SAINT® research, the average time to remission was 2.6 days. Results vary, and a free consultation determines whether you're a candidate.",
  },
];

const hfdRelated: RelatedLink[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "Ampa One™",
    href: "/ampa",
    description: "Camera-guided accelerated TMS in as few as 2 days",
  },
  {
    title: "TMS for Depression",
    href: "/services/depression",
    description: "FDA-cleared TMS for treatment-resistant depression",
  },
  {
    title: "SPRAVATO® (Esketamine)",
    href: "/services/spravato",
    description: "FDA-approved nasal spray for TRD",
  },
  {
    title: "Am I a Candidate?",
    href: "/am-i-a-candidate",
    description: "Find out if accelerated TMS is right for you",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
  {
    title: "Why Am I So Tired All the Time? Depression Fatigue",
    href: "/learn/always-tired-depression-fatigue",
    description: "How depression drains energy and why rest doesn't fix it.",
  },
  {
    title: "Can Depression Cause Physical Pain?",
    href: "/learn/can-depression-cause-physical-pain",
    description: "Why depression hurts and what helps with the aches.",
  },
  {
    title: "Can't Stop Crying for No Reason?",
    href: "/learn/cant-stop-crying-for-no-reason",
    description: "What frequent crying can mean and when to get help.",
  },
];

export const pageHighFunctioningDepression: LearnPageData = {
  slug: "high-functioning-depression-symptoms-treatment",
  title: "High-Functioning Depression Symptoms & Depression Treatment",
  description:
    "Still showing up but feeling flat? Learn 10 quiet depression symptoms and how accelerated depression treatment like 5-day SAINT® TMS can help in Coral Springs.",
  canonical: "/high-functioning-depression-symptoms-treatment",
  h1: "High-Functioning Depression Symptoms: 10 Quiet Signs and Faster Depression Treatment",
  breadcrumbLabel: "High-Functioning Depression",
  crumbs: baseCrumbs("High-Functioning Depression"),
  shortAnswer:
    "High-functioning depression is not a formal diagnosis. The closest clinical match is persistent depressive disorder (PDD), a depressed mood on most days for at least two years. People with it keep working and showing up, so their depression symptoms often go unnoticed for years. If antidepressants haven't helped enough, accelerated depression treatment such as SAINT® TMS (5 days) or Ampa One™ (as few as 2 days) is available at Neuro Wellness TMS Centers of America in Coral Springs, FL.",
  keyTakeaways: [
    "High-functioning depression is not a formal diagnosis. The closest clinical match is persistent depressive disorder (PDD).",
    "People with it keep working and showing up, so their depression symptoms often go unnoticed for years.",
    "Common signs include exhaustion sleep doesn't fix, fading interest, irritability and defaulting to \u201CI'm fine.\u201D",
    "If antidepressants haven't helped enough, accelerated depression treatment such as SAINT® TMS (5 days) or Ampa One™ (as few as 2 days) is available at Neuro Wellness TMS Centers of America in Coral Springs, FL.",
  ],
  sections: [
    {
      heading: "When Depression Doesn't Look Like Depression",
      body: (
        <>
          {P(
            "The depression symptoms most people picture are the visible ones: not getting out of bed, missing work, a life that has clearly stopped. But a quieter form of depression looks almost the opposite. You still answer every email, make every meeting and keep every commitment. From the outside, your life looks fine. Sometimes it even looks impressive.",
          )}
          {P(
            "That is exactly why it goes unnoticed. When you are still functioning, the word \u201Cfine\u201D stretches to cover everything from real well-being to a low hum of suffering that has been running so long it feels like your personality.",
          )}
          {P(
            "Below are 10 everyday habits that can look like being tired, practical or \u201Cjust how you are,\u201D but that clinicians recognize as signs of high-functioning depression. This isn't a diagnosis. It's a description. If it fits, it may be worth taking seriously, especially because today's depression treatment options, including accelerated brain stimulation at Neuro Wellness TMS Centers of America, can work in days rather than months.",
          )}
        </>
      ),
    },
    {
      heading: "What Is High-Functioning Depression?",
      body: (
        <>
          {P(
            "\u201CHigh-functioning depression\u201D isn't a formal diagnosis, but it describes something real. The closest clinical match is persistent depressive disorder (PDD), once called dysthymia: a depressed mood present on most days for at least two years in adults. It is usually milder than a major depressive episode but lasts far longer, sometimes for decades.",
          )}
          {P(
            "The defining feature is that you keep going. Work gets done, relationships hold, nothing sets off alarms. Underneath, there is a steady drain on pleasure, energy, motivation and emotional engagement that is just mild enough to feel normal.",
          )}
          {P(
            "That is what makes it risky. It is rarely the most severe form of depression, but it is the most likely to go unnamed and untreated. Many people with PDD also experience full major depressive episodes on top of it, sometimes called \u201Cdouble depression,\u201D which can make depression treatment more urgent than it first appears.",
          )}
        </>
      ),
    },
  ],
  faqs: hfdFaqs,
  sources: [
    {
      citation:
        "Neuro Wellness TMS Centers of America \u2013 SAINT\u00AE overview",
      url: "https://neurowellnesstms.com/",
    },
    {
      citation: "Neuro Wellness \u2013 Ampa One\u2122",
      url: "https://neurowellnesstms.com/ampa",
    },
    {
      citation:
        "Cole EJ, et al. American Journal of Psychiatry, 2022 (randomized trial)",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. American Journal of Psychiatry, 2020 (open-label study)",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/high-functioning-depression-symptoms-treatment#webpage`,
        name: "High-Functioning Depression Symptoms: 10 Quiet Signs and Faster Depression Treatment",
        url: `${SITE_URL}/high-functioning-depression-symptoms-treatment`,
        description:
          "Still showing up but feeling flat? Learn 10 quiet depression symptoms and how accelerated depression treatment like 5-day SAINT® TMS can help in Coral Springs.",
        about: [
          {
            "@type": "MedicalCondition",
            name: "Persistent depressive disorder",
          },
          {
            "@type": "MedicalCondition",
            name: "Treatment-resistant depression",
          },
        ],
      }),
      orgNode,
      faqJsonld(
        hfdFaqs,
        `${SITE_URL}/high-functioning-depression-symptoms-treatment#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("High-Functioning Depression")),
    ],
  },
  relatedLinks: hfdRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
};
